import {
  forwardRef,
  useId,
  useState,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Mail,
  MapPin,
  Sparkles,
  SquareCheckBig,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui';
import { EventLinkBadge } from '@/components/domain/shared/EventLinkBadge';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { ChildBadge, TaskCard } from '@/pages/review/_components';
import { REVIEW_COPY } from '@/pages/review/reviewCopy';
import type { ReviewListItem } from '@/pages/review/reviewListModel';
import type { Child } from '@/types';
import { ChangeLine } from './ChangeLine';

/** A household member the page can resolve a `memberIds` entry to. */
export interface ReviewItemCardMember {
  id: string;
  displayName: string;
  emoji?: string | null;
  color?: string | null;
  imagePath?: string | null;
}

export interface ReviewItemCardProps {
  item: ReviewListItem;
  /** The single top-right action (Don't add / Don't change / Keep event). */
  onAction(item: ReviewListItem): void;
  /** Tap → the details sheet (AC10). A nested task opens itself. */
  onOpen(item: ReviewListItem): void;
  /** Add date tag on an undated item. */
  onAddDate(item: ReviewListItem): void;
  /**
   * Source line → the details sheet at Sources (AC15). Without it the
   * source line is plain text and a tap opens the card.
   */
  onOpenSources?(item: ReviewListItem): void;
  /**
   * Disables the action and Add date (a Confirm all is in flight: a
   * set-aside or a new date then would be silently lost). Opening the item
   * stays available.
   */
  actionsDisabled?: boolean;
  /**
   * Resolves `item.memberIds` to avatars. Optional: review_items has no
   * member column yet (Step 2 adds the picker), so ids it can't resolve are
   * skipped.
   */
  members?: readonly ReviewItemCardMember[];
  /** For the nested production task cards (child badges resolve against the family). */
  childrenData?: Child[];
  timezone?: string | null;
  /** Each nested task's card element, by task id (the page's focus targets). */
  registerTask?(taskId: string, el: HTMLDivElement | null): void;
}

/** Children and members show two, then `+n`. */
const VISIBLE_PEOPLE = 2;

const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

/** Inner controls must not also open the card. */
function stop(handler: () => void) {
  return (e: MouseEvent) => {
    e.stopPropagation();
    handler();
  };
}

function TypeTile({ type }: { type: ReviewListItem['type'] }) {
  const isEvent = type === 'event';
  const Icon = isEvent ? Calendar : SquareCheckBig;
  return (
    <div className="relative shrink-0">
      <div
        className={[
          'flex size-11 items-center justify-center rounded-md border',
          isEvent
            ? 'border-info-border bg-info-bg'
            : 'border-border bg-surface-secondary',
        ].join(' ')}
      >
        <Icon
          size={20}
          strokeWidth={1.5}
          aria-hidden="true"
          className={isEvent ? 'text-info-text' : 'text-icon-default'}
        />
      </div>
      <Sparkles
        size={16}
        strokeWidth={1.5}
        aria-hidden="true"
        className="absolute -top-2 -left-2 text-icon-default"
      />
    </div>
  );
}

function DetailLine({
  icon: Icon,
  children,
}: {
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <p className="m-0 flex items-start gap-1.5 text-sm text-muted-foreground">
      <Icon
        size={16}
        strokeWidth={1.5}
        aria-hidden="true"
        className="mt-0.5 shrink-0"
      />
      <span className="min-w-0 break-words">{children}</span>
    </p>
  );
}

function Overflow({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="self-center text-xs text-muted-foreground">+{count}</span>
  );
}

function Children({ names }: { names: readonly string[] }) {
  if (names.length === 0) return <span />;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {names.slice(0, VISIBLE_PEOPLE).map((name) => (
        <ChildBadge
          key={name}
          name={name}
        />
      ))}
      <Overflow count={names.length - VISIBLE_PEOPLE} />
    </div>
  );
}

function Members({
  ids,
  members,
}: {
  ids: readonly string[];
  members: readonly ReviewItemCardMember[];
}) {
  const byId = new Map(members.map((m) => [m.id, m]));
  const resolved = [...new Set(ids)]
    .map((id) => byId.get(id))
    .filter((m): m is ReviewItemCardMember => !!m);
  if (resolved.length === 0) return null;
  return (
    <div className="flex shrink-0 items-center gap-1">
      {resolved.slice(0, VISIBLE_PEOPLE).map((m) => (
        <ProfileAvatar
          key={m.id}
          size="sm"
          alt={m.displayName}
          emoji={m.emoji}
          color={m.color}
          imagePath={m.imagePath}
        />
      ))}
      <Overflow count={resolved.length - VISIBLE_PEOPLE} />
    </div>
  );
}

/** One open task a cancellation cancels on confirm (E-8): read-only. */
function CancelTaskLine({
  title,
  whenLabel,
}: {
  title: string;
  whenLabel: string | null;
}) {
  return (
    <div
      data-testid="review-cancel-task"
      className="flex flex-col gap-1 rounded-xl border border-border p-4"
    >
      <p className="m-0 flex items-start gap-1.5 text-sm font-semibold text-foreground">
        <SquareCheckBig
          size={16}
          strokeWidth={1.5}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-muted-foreground"
        />
        <span className="min-w-0 break-words">{title}</span>
      </p>
      {whenLabel && <DetailLine icon={Calendar}>{whenLabel}</DetailLine>}
    </div>
  );
}

interface CardBodyProps extends ReviewItemCardProps {
  cardRef?: Ref<HTMLDivElement>;
}

function CardBody({
  item,
  onAction,
  onOpen,
  onAddDate,
  onOpenSources,
  actionsDisabled = false,
  members = [],
  childrenData,
  timezone,
  registerTask,
  cardRef,
}: CardBodyProps) {
  const [tasksOpen, setTasksOpen] = useState(false);
  const tasksId = useId();
  const titleId = useId();
  const needsDate = item.undatedEvent || item.undatedTask;
  const tagged = item.kind !== 'new';
  // A new event's new tasks, or the open tasks a cancellation cancels (E3).
  const taskCount = item.tasks.length || item.cancelTasks.length;
  const TasksChevron = tasksOpen ? ChevronUp : ChevronDown;

  const action = (
    <button
      type="button"
      // aria-disabled, not native `disabled`: the button keeps keyboard
      // focus while Confirm all runs (a native disable drops it to <body>).
      onClick={stop(() => {
        if (!actionsDisabled) onAction(item);
      })}
      aria-disabled={actionsDisabled || undefined}
      // min-h-6 keeps a 24px tap target; -my-0.5 keeps the row 20px tall.
      className={`-my-0.5 ml-auto inline-flex min-h-6 shrink-0 cursor-pointer items-center rounded-md px-1 text-sm font-medium text-muted-foreground hover:text-foreground aria-disabled:cursor-not-allowed aria-disabled:opacity-50 ${FOCUS_RING}`}
    >
      {item.actionLabel}
    </button>
  );

  const title = (
    // The click bubbles to the card's onOpen; the button gives the card a
    // keyboard path (Enter/Space fire a click).
    <button
      id={titleId}
      type="button"
      className={`min-w-0 cursor-pointer rounded-sm text-left text-base font-semibold break-words text-text-heading ${FOCUS_RING}`}
    >
      {item.title}
    </button>
  );

  const sourceLine = (
    <>
      <Mail
        size={16}
        strokeWidth={1.5}
        aria-hidden="true"
        className="mt-0.5 shrink-0 text-muted-foreground"
      />
      {/* A span, not a div: it may sit inside the source button. */}
      <span className="flex min-w-0 flex-col">
        <span className="min-w-0 truncate text-sm text-foreground">
          {item.source.subject}
        </span>
        {item.source.sender && (
          <span className="min-w-0 truncate text-xs text-text-caption">
            {item.source.sender}
          </span>
        )}
      </span>
    </>
  );

  return (
    <div className="flex w-full flex-col items-start">
      <div
        ref={cardRef}
        tabIndex={-1}
        role="group"
        aria-labelledby={titleId}
        data-testid="review-item-card"
        onClick={(e) => {
          e.stopPropagation();
          onOpen(item);
        }}
        className="flex w-full cursor-pointer flex-col overflow-hidden rounded-xl border border-border bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <div className="flex gap-3 p-4">
          <TypeTile type={item.type} />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {tagged ? (
              <>
                <div className="flex items-center gap-2">
                  <Badge
                    size="xs"
                    variant={item.kind === 'change' ? 'info' : 'danger'}
                  >
                    {item.kind === 'change'
                      ? REVIEW_COPY.tagChange
                      : REVIEW_COPY.tagCancelled}
                  </Badge>
                  {action}
                </div>
                {title}
              </>
            ) : (
              <div className="flex items-start gap-2">
                {title}
                {action}
              </div>
            )}

            <div className="flex flex-col gap-2">
              {item.kind === 'change' && item.changes.length > 0 ? (
                item.changes.map((c) => (
                  <ChangeLine
                    key={c.field}
                    change={c}
                  />
                ))
              ) : item.whenLabel ? (
                <DetailLine icon={Calendar}>{item.whenLabel}</DetailLine>
              ) : (
                // Only a new item can need a date: a cancel or a change never
                // does, even when its target is undated.
                needsDate && (
                  <button
                    type="button"
                    onClick={stop(() => {
                      if (!actionsDisabled) onAddDate(item);
                    })}
                    aria-disabled={actionsDisabled || undefined}
                    className={`w-fit cursor-pointer rounded-full aria-disabled:cursor-not-allowed aria-disabled:opacity-50 ${FOCUS_RING}`}
                  >
                    <Badge
                      size="xs"
                      variant="warning"
                    >
                      {REVIEW_COPY.tagAddDate}
                    </Badge>
                  </button>
                )
              )}
              {item.kind !== 'change' && item.placeLabel && (
                <DetailLine icon={MapPin}>{item.placeLabel}</DetailLine>
              )}
            </div>

            {/* v1.0's badge: the scheduled event this new task joins. */}
            {item.eventBadge && <EventLinkBadge title={item.eventBadge} />}

            {item.note && (
              <p className="m-0 rounded-md bg-danger-light px-2 py-1 text-sm text-text-error">
                {item.note}
              </p>
            )}

            {(item.childNames.length > 0 || item.memberIds.length > 0) && (
              <div className="flex items-end justify-between gap-3">
                <Children names={item.childNames} />
                <Members
                  ids={item.memberIds}
                  members={members}
                />
              </div>
            )}

            {onOpenSources ? (
              <button
                type="button"
                data-testid="review-item-source"
                onClick={stop(() => onOpenSources(item))}
                className={`flex min-w-0 cursor-pointer items-start gap-1.5 rounded-sm text-left ${FOCUS_RING}`}
              >
                {sourceLine}
              </button>
            ) : (
              <div
                data-testid="review-item-source"
                className="flex min-w-0 items-start gap-1.5"
              >
                {sourceLine}
              </div>
            )}
          </div>
        </div>

        {taskCount > 0 && (
          // Production's Related Tasks bar (EventGroupCard): the card closes here.
          <button
            type="button"
            aria-expanded={tasksOpen}
            aria-controls={tasksId}
            onClick={stop(() => setTasksOpen((v) => !v))}
            // Inset ring: the card's overflow-hidden would clip an outer one.
            className="flex w-full cursor-pointer items-center justify-between border-t border-border bg-surface-secondary px-5 py-2 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
          >
            <span className="flex items-center gap-1.5">
              <SquareCheckBig
                size={20}
                aria-hidden="true"
                className="text-muted-foreground"
              />
              <span className="text-sm font-bold text-muted-foreground">
                {REVIEW_COPY.tasksRow(taskCount)}
              </span>
            </span>
            <TasksChevron
              size={20}
              aria-hidden="true"
              className="text-muted-foreground"
            />
          </button>
        )}
      </div>

      {taskCount > 0 && tasksOpen && (
        // Below the card, behind the line, as production's nested tasks.
        <div
          id={tasksId}
          className="flex w-full items-stretch"
        >
          <div className="flex w-10 flex-shrink-0 justify-center">
            <div className="w-px bg-border" />
          </div>
          {/* min-w-0: a long unbreakable string must not widen the column. */}
          <div className="flex min-w-0 flex-1 flex-col gap-3 pt-3">
            {item.tasks.map((task) => (
              <TaskCard
                key={task.id}
                item={task.row}
                // The event as it is on the schedule: a change card's title
                // is its target's (F-1); a new event's is its row's.
                parentEvent={{ ...item.row, title: item.title }}
                childrenData={childrenData ?? []}
                timezone={timezone}
                onEditItem={() => onOpen(task)}
                onDontAdd={() => {
                  if (!actionsDisabled) onAction(task);
                }}
                dontAddDisabled={actionsDisabled}
                cardRef={(el) => registerTask?.(task.id, el)}
              />
            ))}
            {item.cancelTasks.map((task) => (
              <CancelTaskLine
                key={task.id}
                title={task.title}
                whenLabel={task.whenLabel}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Review Item Card (Review Flow v2.0, Figma 11533-7024) — one item on the
 * Review v0 list (YAR-551).
 *
 * Three variants from `ReviewListItem.kind`: `new` puts the action on the
 * title row; `change` and `cancelled` put a tag and the action on their own
 * row above the title. A change card lists each changed field (old struck
 * through, new bold) instead of the date and place; a cancelled card adds
 * the red note. A new or change event's tasks (production task cards, each
 * with its own Don't add and the event badge with the schedule title) or a
 * cancellation's open tasks (read-only lines) hang below the card under the
 * tasks row, closed by default. A top-level new task on an event already on
 * the schedule carries v1.0's event badge.
 *
 * Presentational: the page (C4) owns the list, the action, Add date and
 * opening the details sheet. The ref is the AC5/AC6 focus target.
 */
export const ReviewItemCard = forwardRef<HTMLDivElement, ReviewItemCardProps>(
  function ReviewItemCard(props, ref) {
    return (
      <CardBody
        {...props}
        cardRef={ref}
      />
    );
  },
);
